import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';

import CheckCircle from '@components/CheckCircle';

import { EXTENSION_URL } from '@constants/extension-codes';
import { DIRECTIVES_ID } from '@constants/survey-ids';

import { useFeatureFlags } from '@hooks/useFeatureFlags';

import type { CategoryType } from '@models/legal-directive';

import {
  allOrNoneSelected,
  consentToAllMap,
  consentToNoneMap,
  getLegalDirMap,
} from '@utils/legalDirective';

import type { FormValues } from '../LegalSurvey';

interface LegalSelectorProps {
  type: CategoryType;
}

export default function LegalSelector({ type }: LegalSelectorProps) {
  const { setFieldValue, values } = useFormikContext<FormValues>();
  const { flags } = useFeatureFlags();
  const map = getLegalDirMap(type);
  const options = Object.keys(map);

  const handleClick = (identifier: string) => {
    const selectedUrl = `${EXTENSION_URL}${DIRECTIVES_ID}/${type}/${identifier}`;
    const isUnselected = values.selections.some(
      ({ url }) => url === selectedUrl
    );
    const isAllOrNoneSelected = allOrNoneSelected(identifier, type);

    // 1. All or non option was (un)selected
    if (isAllOrNoneSelected) {
      if (isUnselected) {
        setFieldValue('selections', []);
      } else {
        setFieldValue('selections', [
          {
            url: selectedUrl,
            valueBoolean: true,
          },
        ]);
      }
      // 2. Existing option was unselected
    } else if (isUnselected) {
      setFieldValue(
        'selections',
        values.selections.filter(({ url }) => url !== selectedUrl)
      );

      // 3. New option was selected
    } else {
      setFieldValue('selections', [
        ...values.selections,
        {
          url: selectedUrl,
          valueBoolean: true,
        },
      ]);
    }
  };

  const isAllSelected = values.selections.some(({ url }) => {
    const allConsented = consentToAllMap[type];
    return url.endsWith(allConsented);
  });
  const isNoneSelected = values.selections.some(({ url }) => {
    const noneConsented = consentToNoneMap[type];
    return url.endsWith(noneConsented);
  });
  const isAllOrNoneSelected = isAllSelected || isNoneSelected;

  return (
    <List>
      {options.map((identifier, index) => {
        const friendlyText = map[identifier];
        const isSelected = values.selections.find(({ url }) =>
          url.endsWith(identifier)
        )?.valueBoolean;

        // Is this option we're looping over 'All' or 'None'?
        const isOptionAllOrNone = allOrNoneSelected(identifier, type);

        // If 'All' or 'None' is selected, then all other options excluding those should be disabled
        const disabled = isAllOrNoneSelected ? !isOptionAllOrNone : false;

        // If 'All' option is chosen, specific options should be selected and disabled
        const isOptionSelected =
          (isAllSelected && !isOptionAllOrNone ? true : isSelected) ?? false;
        return (
          <ListItemButton
            disabled={
              disabled ||
              !flags.isLegalDirectiveCreateConsentEnabled ||
              !flags.isLegalDirectiveUpdateConsentEnabled
            }
            key={index}
            onClick={() => handleClick(identifier)}
          >
            <ListItemIcon>
              <CheckCircle selected={isOptionSelected} size={25} />
            </ListItemIcon>
            <ListItemText primary={friendlyText} />
          </ListItemButton>
        );
      })}
    </List>
  );
}
